// src/pages/DriverCalendarPage.js
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";
import { useNavigate } from "react-router-dom";

const TYPE_COLORS = {
  move:      { bg: "#1d4ed8", fg: "#fff", label: "On Move"   },
  scheduled: { bg: "#0d9488", fg: "#fff", label: "Scheduled" },
  time_off:  { bg: "#b45309", fg: "#fff", label: "Time Off"  },
  holiday:   { bg: "#b91c1c", fg: "#fff", label: "Holiday"   },
};

const STATUS_DISPLAY = {
  available: { bg: "#16a34a", label: "Available" },
  on_move:   { bg: "#1d4ed8", label: "On Move"   },
  off_duty:  { bg: "#6b7280", label: "Off Duty"  },
};

const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

function daysInMonth(y, m)    { return new Date(y, m + 1, 0).getDate(); }
function firstDayOfMonth(y, m){ return new Date(y, m, 1).getDay(); }
function pad2(n)               { return String(n).padStart(2, "0"); }
function dateStr(y, m, d)     { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }
function today()              { return new Date().toISOString().slice(0, 10); }
function addDays(ds, n) {
  const [y, m, d] = ds.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}
function buildCells(y, m) {
  const first = firstDayOfMonth(y, m);
  const days  = daysInMonth(y, m);
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(dateStr(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
function initMonth() {
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() };
}

// ── Assign Driver to an existing move ────────────────────────────────────────
function AssignDriverModal({ move, drivers, onClose, onSaved }) {
  const [driverId,  setDriverId]  = useState("");
  const [startDate, setStartDate] = useState(today());
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState("");

  const save = async () => {
    if (!driverId) return setErr("Select a driver.");
    setBusy(true); setErr("");
    try {
      await api.post(`/moves/${move.TripId}/assign-driver`, {
        driver_id:  Number(driverId),
        start_date: startDate,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to assign driver.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginBottom: 4 }}>Assign Driver to Move #{move.TripId}</h2>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>
        {move.destination}{move.rowo ? ` · ${move.rowo}` : ""}
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Driver</label>
          <select className="input" value={driverId} onChange={e => setDriverId(e.target.value)}>
            <option value="">— Select driver —</option>
            {drivers.map(d => (
              <option key={d.DriverId} value={d.DriverId}>{d.FullName}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Start Date</label>
          <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Assign Driver"}
          </button>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Schedule / Block Modal ────────────────────────────────────────────────────
function AddBlockModal({ drivers, onClose, onSaved }) {
  const [driverId,    setDriverId]  = useState(drivers[0]?.DriverId || "");
  const [type,        setType]      = useState("scheduled_move");
  const [startDate,   setStart]     = useState(today());
  const [endDate,     setEnd]       = useState(today());
  const [reason,      setReason]    = useState("");
  const [destination, setDest]      = useState("");
  const [rowo,        setRowo]      = useState("");
  const [notes,       setNotes]     = useState("");
  const [busy,        setBusy]      = useState(false);
  const [err,         setErr]       = useState("");

  const isMove = type === "scheduled_move";

  const save = async () => {
    if (!driverId) return setErr("Select a driver.");
    setBusy(true); setErr("");
    try {
      if (isMove) {
        if (!destination.trim()) return setErr("Destination is required.");
        await api.post("/moves", {
          driver_id:   Number(driverId),
          start_date:  startDate,
          end_date:    endDate > startDate ? endDate : undefined,
          Destination: destination.trim(),
          Notes:       notes.trim()  || undefined,
          rowo:        rowo.trim()   || undefined,
        });
      } else {
        if (startDate > endDate) return setErr("End date must be ≥ start date.");
        await api.post("/driver/unavailability", {
          driver_id:  Number(driverId),
          start_date: startDate,
          end_date:   endDate,
          type,
          reason: reason.trim() || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginBottom: 16 }}>Schedule / Block Driver</h2>
      <div style={{ display: "grid", gap: 12 }}>

        <div>
          <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Driver</label>
          <select className="input" value={driverId} onChange={e => setDriverId(e.target.value)}>
            {drivers.map(d => <option key={d.DriverId} value={d.DriverId}>{d.FullName}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Type</label>
          <select className="input" value={type} onChange={e => setType(e.target.value)}>
            <option value="scheduled_move">Scheduled Move / Delivery</option>
            <option value="time_off">Time Off</option>
            <option value="holiday">Holiday</option>
          </select>
        </div>

        {isMove ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Start Date</label>
                <input type="date" className="input" value={startDate} onChange={e => setStart(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Est. End Date</label>
                <input type="date" className="input" value={endDate} onChange={e => setEnd(e.target.value)} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Destination *</label>
              <input className="input" placeholder="e.g. City of Edmonton, Calgary Yard" value={destination} onChange={e => setDest(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>ROWO / Unit (optional)</label>
              <input className="input" placeholder="e.g. 102192 or VIN last-8" value={rowo} onChange={e => setRowo(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Notes (optional)</label>
              <input className="input" placeholder="Any additional details" value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Start Date</label>
                <input type="date" className="input" value={startDate} onChange={e => setStart(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>End Date</label>
                <input type="date" className="input" value={endDate} onChange={e => setEnd(e.target.value)} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>Reason (optional)</label>
              <input className="input" placeholder="e.g. Vacation, Sick leave, Statutory holiday" value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </>
        )}

        {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn" onClick={save} disabled={busy}>
            {busy ? "Saving…" : isMove ? "Schedule Move" : "Save Block"}
          </button>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Event chip ────────────────────────────────────────────────────────────────
function Chip({ event, onDelete, onCancelMove, onReleaseDriver, onAssignDriver }) {
  const [open, setOpen] = useState(false);
  const col  = TYPE_COLORS[event.type] || TYPE_COLORS.time_off;
  const name = event.driver?.FullName?.split(" ")[0] || "?";
  const m    = event.move;

  const close = () => setOpen(false);

  return (
    <div style={{ position: "relative" }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          background: col.bg, color: col.fg, borderRadius: 4,
          padding: "2px 6px", fontSize: 11, fontWeight: 700,
          cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis", maxWidth: "100%",
        }}
        title={`${event.driver?.FullName} — ${col.label}${m?.destination ? ` → ${m.destination}` : ""}${event.block?.reason ? `: ${event.block.reason}` : ""}`}
      >
        {name} · {col.label}
      </div>

      {open && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, zIndex: 100,
            background: "var(--card-bg)", border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 8, padding: 10, minWidth: 210, boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ fontWeight: 900, marginBottom: 2 }}>{event.driver?.FullName}</div>
          <div style={{ fontSize: 12, color: col.bg, fontWeight: 700, marginBottom: 8 }}>{col.label}</div>

          {(event.type === "move" || event.type === "scheduled") && m && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Move #{m.TripId}<br />
              {m.destination && <>→ {m.destination}<br /></>}
              {m.rowo && <>ROWO: {m.rowo}<br /></>}
              Start: {(m.md_start_date || m.move_start_date || "—").slice(0, 10)}<br />
              {m.md_end_date
                ? `End: ${m.md_end_date.slice(0, 10)}`
                : m.move_end_date
                  ? `Est. end: ${m.move_end_date.slice(0, 10)}`
                  : m.completed_at
                    ? `Completed: ${m.completed_at.slice(0, 10)}`
                    : <span style={{ color: col.bg }}>In progress</span>
              }
            </div>
          )}

          {(event.type === "time_off" || event.type === "holiday") && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              {event.block?.start_date} → {event.block?.end_date}<br />
              {event.block?.reason || "No reason provided"}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Move actions */}
            {(event.type === "move" || event.type === "scheduled") && m && (
              <>
                <button
                  style={{ fontSize: 12, color: "#f59e0b", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                  onClick={() => { onAssignDriver(m); close(); }}
                >
                  {event.type === "scheduled" ? "Change driver" : "Reassign driver"}
                </button>
                <button
                  style={{ fontSize: 12, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                  onClick={() => { onReleaseDriver(m.TripId); close(); }}
                >
                  Release driver from move
                </button>
                {event.type === "scheduled" && (
                  <button
                    style={{ fontSize: 12, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                    onClick={() => { onCancelMove(m.TripId); close(); }}
                  >
                    Cancel move entirely
                  </button>
                )}
              </>
            )}
            {/* Time off / holiday actions */}
            {(event.type === "time_off" || event.type === "holiday") && (
              <button
                style={{ fontSize: 12, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                onClick={() => { onDelete(event.block.id); close(); }}
              >
                Delete block
              </button>
            )}
            <button
              style={{ fontSize: 12, background: "none", border: "none", cursor: "pointer", color: "var(--fg)", padding: 0, opacity: 0.4, textAlign: "left" }}
              onClick={close}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DriverCalendarPage() {
  const nav = useNavigate();
  const [{ year, month }, setYM] = useState(initMonth);
  const [drivers,    setDrivers]   = useState([]);
  const [moves,      setMoves]     = useState([]);
  const [unavail,    setUnavail]   = useState([]);
  const [openMoves,  setOpenMoves] = useState([]);
  const [loading,    setLoading]   = useState(false);
  const [err,        setErr]       = useState("");
  const [showAdd,    setShowAdd]   = useState(false);
  const [assignMove, setAssignMove] = useState(null); // move object for AssignDriverModal

  const fromDate = dateStr(year, month, 1);
  const toDate   = dateStr(year, month, daysInMonth(year, month));

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/driver/calendar?from=${fromDate}&to=${toDate}`);
      setDrivers(data.drivers        || []);
      setMoves(data.moves            || []);
      setUnavail(data.unavailability || []);
      setOpenMoves(data.open_moves   || []);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load calendar.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => setYM(({ year: y, month: m }) => m === 0  ? { year: y - 1, month: 11 } : { year: y, month: m - 1 });
  const nextMonth = () => setYM(({ year: y, month: m }) => m === 11 ? { year: y + 1, month: 0  } : { year: y, month: m + 1 });
  const goToday   = () => setYM(initMonth());

  const deleteBlock = async (id) => {
    try { await api.delete(`/driver/unavailability/${id}`); await load(); }
    catch (e) { alert(e?.response?.data?.error || "Delete failed."); }
  };

  const cancelMove = async (tripId) => {
    if (!window.confirm("Delete this move entirely?")) return;
    try { await api.delete(`/moves/${tripId}`); await load(); }
    catch (e) { alert(e?.response?.data?.error || "Cancel failed."); }
  };

  const releaseDriver = async (tripId) => {
    if (!window.confirm("Release the driver from this move? The move stays open.")) return;
    try { await api.post(`/moves/${tripId}/release-driver`); await load(); }
    catch (e) { alert(e?.response?.data?.error || "Release failed."); }
  };

  // ── Build event map ────────────────────────────────────────────────────────
  const eventsByDate = {};
  const todayStr = today();

  for (const m of moves) {
    // m comes from move_drivers JOIN moves — use per-driver dates
    const driver = drivers.find(d => String(d.DriverId) === String(m.driver_id));
    const start  = (m.md_start_date || "").slice(0, 10);
    if (!start || !driver) continue;

    const isFuture = start > todayStr;
    const type     = isFuture ? "scheduled" : "move";

    // End: use driver's assignment end, then move expected end, then completed, then today
    const rawEnd = m.md_end_date
      ? m.md_end_date.slice(0, 10)
      : m.move_end_date
        ? m.move_end_date.slice(0, 10)
        : m.completed_at
          ? m.completed_at.slice(0, 10)
          : todayStr;
    const end = rawEnd;

    const lo = start < fromDate ? fromDate : start;
    const hi = end   > toDate   ? toDate   : end;
    let cur  = lo;
    while (cur <= hi) {
      if (!eventsByDate[cur]) eventsByDate[cur] = [];
      eventsByDate[cur].push({ type, driver, move: m });
      if (cur >= hi) break;
      cur = addDays(cur, 1);
    }
  }

  for (const u of unavail) {
    const driver = drivers.find(d => String(d.DriverId) === String(u.driver_id));
    const lo = u.start_date < fromDate ? fromDate : u.start_date;
    const hi = u.end_date   > toDate   ? toDate   : u.end_date;
    let cur  = lo;
    while (cur <= hi) {
      if (!eventsByDate[cur]) eventsByDate[cur] = [];
      eventsByDate[cur].push({ type: u.type, driver, block: u });
      if (cur >= hi) break;
      cur = addDays(cur, 1);
    }
  }

  const cells = buildCells(year, month);

  return (
    <div className="container" style={{ maxWidth: "100%", paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn-outline" style={{ fontSize: 13 }} onClick={() => nav("/admin/driver-mgmt")}>← Drivers</button>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Driver Calendar</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {Object.entries(TYPE_COLORS).map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: v.bg }} />
                {v.label}
              </div>
            ))}
          </div>
          <button className="btn-outline" onClick={goToday} style={{ fontSize: 13 }}>Today</button>
          <button className="btn" onClick={() => setShowAdd(true)}>+ Schedule / Block</button>
        </div>
      </div>

      {/* Month nav */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button className="btn-outline" onClick={prevMonth} style={{ fontSize: 18, lineHeight: 1, padding: "4px 12px" }}>‹</button>
        <div style={{ fontWeight: 900, fontSize: 20, minWidth: 200, textAlign: "center" }}>
          {MONTHS[month]} {year}
        </div>
        <button className="btn-outline" onClick={nextMonth} style={{ fontSize: 18, lineHeight: 1, padding: "4px 12px" }}>›</button>
        {loading && <span className="muted" style={{ fontSize: 13 }}>Loading…</span>}
      </div>

      {err && <div style={{ color: "#ef4444", marginBottom: 12, fontSize: 14 }}>{err}</div>}

      {/* Calendar grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden",
      }}>
        {DAYS.map(d => (
          <div key={d} style={{
            padding: "8px 4px", textAlign: "center", fontWeight: 700, fontSize: 12,
            background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}>{d}</div>
        ))}
        {cells.map((date, i) => {
          const events  = date ? (eventsByDate[date] || []) : [];
          const isToday = date === todayStr;
          return (
            <div key={i} style={{
              minHeight: 90, padding: "4px 6px",
              borderRight:  (i + 1) % 7 === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              background:   isToday ? "rgba(59,130,246,0.08)" : "transparent",
              position:     "relative",
            }}>
              {date && (
                <>
                  <div style={{
                    fontSize: 12, marginBottom: 4,
                    width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "50%",
                    background: isToday ? "var(--accent, #4183e6)" : "transparent",
                    color:      isToday ? "#fff" : "var(--fg)",
                    fontWeight: isToday ? 900 : 400,
                  }}>
                    {parseInt(date.split("-")[2], 10)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {events.map((ev, ei) => (
                      <Chip
                        key={ei}
                        event={ev}
                        onDelete={deleteBlock}
                        onCancelMove={cancelMove}
                        onReleaseDriver={releaseDriver}
                        onAssignDriver={setAssignMove}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Moves needing a driver */}
      {openMoves.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 900, marginBottom: 10, color: "#f59e0b" }}>
            Moves Needing a Driver ({openMoves.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {openMoves.map(m => (
              <div key={m.TripId} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "8px 12px",
                background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
                borderRadius: 8,
              }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Move #{m.TripId}</span>
                  {m.destination && <span style={{ marginLeft: 8, fontSize: 13 }}>→ {m.destination}</span>}
                  {m.rowo && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.6 }}>ROWO: {m.rowo}</span>}
                  {m.start_date && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.6 }}>{m.start_date.slice(0, 10)}</span>}
                </div>
                <button
                  className="btn"
                  style={{ fontSize: 12, padding: "4px 12px", whiteSpace: "nowrap" }}
                  onClick={() => setAssignMove(m)}
                >
                  Assign Driver
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Driver Status Today */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Driver Status Today</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {drivers.map(d => {
            const s   = d.driver_status || "available";
            const col = STATUS_DISPLAY[s] || STATUS_DISPLAY.available;
            return (
              <div key={d.DriverId} style={{
                padding: "8px 14px", borderRadius: 8,
                background: col.bg + "22",
                border: `1px solid ${col.bg}55`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{d.FullName}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: col.bg, marginTop: 2 }}>
                  {col.label}
                </div>
                {d.current_location && (
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                    📍 {d.current_location}
                  </div>
                )}
              </div>
            );
          })}
          {drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {/* Modals */}
      {showAdd && (
        <AddBlockModal
          drivers={drivers}
          onClose={() => setShowAdd(false)}
          onSaved={load}
        />
      )}
      {assignMove && (
        <AssignDriverModal
          move={assignMove}
          drivers={drivers}
          onClose={() => setAssignMove(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
